"use client";

import React, { useRef, useEffect, useState } from "react";

interface SixDigitOtpInputProps {
  value: string;
  onChange: (otp: string) => void;
  error?: string;
  isDisabled?: boolean;
  autoFocus?: boolean;
  label?: string;
  onComplete?: (otp: string) => void;
}

const SixDigitOtpInput: React.FC<SixDigitOtpInputProps> = ({
  value = "",
  onChange,
  error,
  isDisabled = false,
  autoFocus = true,
  label = "Enter 6-Digit OTP",
  onComplete,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Pad or trim value into an array of 6 characters
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus && !isDisabled) {
      // Focus first empty slot or first slot
      const firstEmpty = digits.findIndex((d) => !d);
      const target = firstEmpty === -1 ? 0 : firstEmpty;
      inputsRef.current[target]?.focus();
    }
  }, []);

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (isDisabled) return;
    const rawVal = e.target.value;
    const cleanDigits = rawVal.replace(/\D/g, "");

    if (!cleanDigits) {
      // User erased value
      const newOtpArr = [...digits];
      newOtpArr[index] = "";
      onChange(newOtpArr.join(""));
      return;
    }

    if (cleanDigits.length > 1) {
      // User pasted or browser autofilled multiple characters
      const pasted = cleanDigits.slice(0, 6);
      onChange(pasted);
      const nextTarget = Math.min(pasted.length, 5);
      inputsRef.current[nextTarget]?.focus();
      if (pasted.length === 6 && onComplete) {
        onComplete(pasted);
      }
      return;
    }

    // Single digit entered
    const char = cleanDigits.slice(-1);
    const newOtpArr = [...digits];
    newOtpArr[index] = char;
    const nextOtp = newOtpArr.join("");
    onChange(nextOtp);

    // Auto-focus next input
    if (index < 5) {
      inputsRef.current[index + 1]?.focus();
      inputsRef.current[index + 1]?.select();
    }

    if (nextOtp.length === 6 && onComplete) {
      onComplete(nextOtp);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (isDisabled) return;

    if (e.key === "Backspace") {
      if (digits[index]) {
        // Clear current box
        const newOtpArr = [...digits];
        newOtpArr[index] = "";
        onChange(newOtpArr.join(""));
        e.preventDefault();
      } else if (index > 0) {
        // Move back and clear previous box
        inputsRef.current[index - 1]?.focus();
        const newOtpArr = [...digits];
        newOtpArr[index - 1] = "";
        onChange(newOtpArr.join(""));
        e.preventDefault();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
      inputsRef.current[index - 1]?.select();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputsRef.current[index + 1]?.focus();
      inputsRef.current[index + 1]?.select();
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text/plain");
    const clean = pastedText.replace(/\D/g, "").slice(0, 6);
    if (clean) {
      onChange(clean);
      const targetIndex = Math.min(clean.length, 5);
      inputsRef.current[targetIndex]?.focus();
      if (clean.length === 6 && onComplete) {
        onComplete(clean);
      }
    }
  };

  return (
    <div className="w-full">
      {/* Label and digit counter */}
      <label className="text-xs font-semibold text-(--text,#334155) tracking-wide uppercase mb-2 block">
        {label}
      </label>

      {/* 6 Segmented Digit Boxes */}
      <div className="flex items-center justify-between gap-1.5 xs:gap-2">
        {digits.map((digit, idx) => {
          const isFocused = focusedIndex === idx;
          const isFilled = Boolean(digit);
          const hasError = Boolean(error);

          return (
            <React.Fragment key={idx}>
              <div
                className={`relative flex-1 max-w-14 ${
                  idx === 2 ? "mr-1 sm:mr-2" : ""
                }`}
              >
                <input
                  ref={(el) => {
                    inputsRef.current[idx] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete={idx === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  disabled={isDisabled}
                  value={digit}
                  onChange={(e) => handleChange(idx, e)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  onPaste={handlePaste}
                  onFocus={(e) => {
                    setFocusedIndex(idx);
                    e.target.select();
                  }}
                  onBlur={() => {
                    setFocusedIndex((prev) => (prev === idx ? null : prev));
                  }}
                  aria-label={`Digit ${idx + 1} of 6`}
                  className={`w-full aspect-4/5 sm:aspect-square text-center font-bold text-xl sm:text-2xl rounded-xl sm:rounded-2xl border-2 transition outline-hidden select-none disabled:opacity-50 disabled:cursor-not-allowed ${
                    hasError
                      ? "border-red-400 bg-[#ff000010] text-red-600"
                      : isFocused
                        ? "border-(--secondary,#1A51AA) bg-white text-slate-900 shadow-xs scale-[1.02]"
                        : isFilled
                          ? "border-(--secondary,#1A51AA)/80 bg-[rgb(var(--primary-alpha,26_81_170)/0.06)] text-(--secondary,#1A51AA) shadow-xs"
                          : "border-slate-200 bg-slate-50/70 text-slate-800 hover:border-slate-300 hover:bg-white"
                  }`}
                />

                {/* Subtle indicator dot when empty and unfocused */}
                {!digit && !isFocused && !hasError && (
                  <span className="absolute inset-0 m-auto w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-[#c7d1df] pointer-events-none" />
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500 mt-2.5 flex items-center gap-1.5 font-medium">
          <svg
            className="w-3.5 h-3.5 shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

export default SixDigitOtpInput;
